import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/shadcn/dialog';
import { Button } from '../../ui/shadcn/button';
import { Checkbox } from '../../ui/shadcn/checkbox';
import {
  AlarmClock, CheckCircle2, Notebook, Loader2, AlertCircle, ExternalLink, Inbox,
  Trash2, Pencil, Sparkles, X as XIcon,
} from 'lucide-react';
import monitoringService, {
  MonitoringKpi, MonitoringScope, DrillDownPayload, DrillDownItem, DrillDownGroup,
} from '../../../services/monitoring';
import { colors, spacing, borderRadius, typography } from '../../../styles/design-tokens';
import { getStatusLabel } from '../../../constants/statusLabels';
import BulkStatusDialog from './BulkStatusDialog';
import BulkDeleteDialog from './BulkDeleteDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  kpi: MonitoringKpi | null;
  scope: MonitoringScope;
  teamId?: string;
}

const KPI_META: Record<MonitoringKpi, { label: string; icon: React.ReactNode; color: string; bgSoft: string }> = {
  pending: { label: '미처리 총합', icon: <Notebook size={20} />, color: '#0f172a', bgSoft: '#f1f5f9' },
  overdue: { label: '7일 초과', icon: <AlarmClock size={20} />, color: '#ef4444', bgSoft: '#fef2f2' },
  completed_today: { label: '오늘 완료', icon: <CheckCircle2 size={20} />, color: '#10b981', bgSoft: '#ecfdf5' },
};

const MENU_COLOR: Record<string, string> = {
  sales: '#3b82f6', purchase: '#f59e0b', production: '#8b5cf6',
  claims: '#ef4444', home_styling: '#ec4899',
};

const SCOPE_LABEL: Record<MonitoringScope, string> = { me: '내 업무', team: '우리 팀', global: '전사' };

const STATUS_CHANGE_SUPPORTED = new Set(['sales', 'purchase', 'production']);

type ItemWithMenu = DrillDownItem & { menu: string; menu_label: string };

const keyOf = (menu: string, id: string) => `${menu}:${id}`;

const DrillDownModal: React.FC<Props> = ({ open, onClose, kpi, scope, teamId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<DrillDownPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!kpi) return;
    setLoading(true);
    setError('');
    try {
      const res = await monitoringService.getDrillDownItems({ kpi, scope, teamId });
      setData(res);
      setSelectedKeys(new Set()); // 새로 로드 시 선택 초기화
    } catch (e: any) {
      setError(e?.message || '데이터 조회 실패');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [kpi, scope, teamId]);

  useEffect(() => {
    if (open && kpi) load();
    else if (!open) { setData(null); setSelectedKeys(new Set()); }
  }, [open, kpi, load]);

  const allItems: ItemWithMenu[] = useMemo(() => {
    if (!data) return [];
    return data.groups.flatMap((g) => g.items.map((it) => ({ ...it, menu: g.menu, menu_label: g.menu_label })));
  }, [data]);

  const selectedItems = useMemo(
    () => allItems.filter((it) => selectedKeys.has(keyOf(it.menu, it.id))),
    [allItems, selectedKeys],
  );

  const menusInSelection = useMemo(() => new Set(selectedItems.map((it) => it.menu)), [selectedItems]);
  const sameMenu = menusInSelection.size === 1;
  const singleMenu = sameMenu ? [...menusInSelection][0] : null;
  const statusChangeSupported = !!(singleMenu && STATUS_CHANGE_SUPPORTED.has(singleMenu));

  const toggleItem = (menu: string, id: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = keyOf(menu, id);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggleGroup = (group: DrillDownGroup) => {
    const groupKeys = group.items.map((it) => keyOf(group.menu, it.id));
    const allSelected = groupKeys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) groupKeys.forEach((k) => next.delete(k));
      else groupKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedKeys.size === allItems.length) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(allItems.map((it) => keyOf(it.menu, it.id))));
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const meta = kpi ? KPI_META[kpi] : null;

  const handleOpenDetail = (url: string) => { navigate(url); onClose(); };

  const handleAIRequest = () => {
    // 선택한 항목의 컨텍스트 요약 — 기본 질문 프롬프트는 넣지 않음 (사용자가 직접 입력)
    const summary = selectedItems
      .map((it) => `- [${it.menu_label}] ${it.title}${it.customer_name ? ` (${it.customer_name})` : ''} / 상태: ${getStatusLabel(it.menu, it.status)} / 경과 ${it.age_days}일`)
      .join('\n');
    const prompt = `[선택된 ${selectedItems.length}건 컨텍스트]\n${summary}\n\n`;
    const context = {
      items: selectedItems.map((it) => ({
        menu: it.menu, menu_label: it.menu_label, id: it.id,
        title: it.title, status: it.status, customer_name: it.customer_name, age_days: it.age_days,
      })),
      kpi, count: selectedItems.length, source: 'team_monitoring_drilldown',
    };
    sessionStorage.setItem('chat:prefill', JSON.stringify({ prompt, context, timestamp: Date.now() }));
    onClose();
    navigate('/ontology/chat?source=monitoring');
  };

  const allSelected = selectedKeys.size > 0 && selectedKeys.size === allItems.length;
  const partialSelected = selectedKeys.size > 0 && selectedKeys.size < allItems.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl sm:max-w-3xl"
        style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <DialogHeader>
          <DialogTitle style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            fontSize: typography.heading.h3.fontSize, color: meta?.color || colors.text.primary,
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: borderRadius.md,
              background: meta?.bgSoft || colors.background.page, color: meta?.color,
            }}>
              {meta?.icon}
            </span>
            {meta?.label || '상세 목록'}
            {data && (
              <span style={{
                fontSize: 14, fontWeight: 500, color: colors.text.secondary,
                padding: '2px 10px', borderRadius: 999,
                background: colors.background.page, marginLeft: 4,
              }}>
                총 {data.total}건
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            <span style={{ color: colors.text.secondary }}>
              조회 범위: <strong style={{ color: colors.text.primary }}>{SCOPE_LABEL[scope]}</strong>
              {kpi === 'overdue' && ' · 접수 후 7일 이상 미처리 건만'}
              {kpi === 'completed_today' && ' · 오늘 자정 이후 완료 처리된 건'}
            </span>
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            padding: spacing.md, background: 'var(--ws-red-bg)',
            border: `1px solid ${colors.destructive}`, color: colors.destructive,
            borderRadius: borderRadius.md, fontSize: 13,
          }}>
            <AlertCircle size={16} />{error}
          </div>
        )}

        {/* 본문 — 스크롤 */}
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, position: 'relative' }}>
          {/* Sticky bulk toolbar */}
          {selectedKeys.size > 0 && (
            <div style={{
              position: 'sticky', top: 0, zIndex: 10,
              background: '#eef2ff',
              border: `1px solid #c7d2fe`,
              borderRadius: borderRadius.md,
              padding: `${spacing.sm} ${spacing.md}`,
              marginBottom: spacing.sm,
              display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap',
            }}>
              <Checkbox
                checked={allSelected}
                data-state={partialSelected ? 'indeterminate' : undefined}
                onCheckedChange={toggleAll}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4338ca' }}>
                {selectedKeys.size}건 선택됨
                {!sameMenu && (
                  <span style={{ fontSize: 11, color: colors.text.muted, fontWeight: 400, marginLeft: spacing.sm }}>
                    (여러 메뉴 혼합)
                  </span>
                )}
              </span>
              <div style={{ flex: 1 }} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusDialogOpen(true)}
                disabled={!statusChangeSupported}
                title={
                  !sameMenu ? '같은 메뉴 항목만 선택 시 사용 가능' :
                  !statusChangeSupported ? '이 메뉴는 일괄 상태 변경 미지원 — 개별 수정' : ''
                }
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Pencil size={13} /> 상태 변경
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: colors.destructive, borderColor: 'var(--ws-red-line)' }}
              >
                <Trash2 size={13} /> 삭제
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAIRequest}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#7c3aed', borderColor: '#c4b5fd' }}
              >
                <Sparkles size={13} /> AI에게 요청하기
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <XIcon size={13} /> 해제
              </Button>
            </div>
          )}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: `${spacing.xl} 0` }}>
              <Loader2 size={24} className="animate-spin" color={colors.text.muted} />
            </div>
          )}

          {!loading && data && data.total === 0 && (
            <div style={{ textAlign: 'center', padding: `${spacing['2xl']} 0` }}>
              <Inbox size={40} color={colors.text.muted} style={{ marginBottom: spacing.sm }} />
              <div style={{ color: colors.text.secondary, fontSize: 14 }}>표시할 항목이 없습니다.</div>
            </div>
          )}

          {!loading && data && data.groups.map((group) => {
            const groupKeys = group.items.map((it) => keyOf(group.menu, it.id));
            const groupSelectedCount = groupKeys.filter((k) => selectedKeys.has(k)).length;
            const groupAllSelected = groupSelectedCount === group.items.length;
            const groupPartial = groupSelectedCount > 0 && !groupAllSelected;

            return (
              <section key={group.menu} style={{ marginBottom: spacing.lg }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: spacing.sm,
                  padding: `${spacing.sm} 0`,
                  borderBottom: `1px solid ${colors.border.light}`,
                  marginBottom: spacing.xs,
                }}>
                  <Checkbox
                    checked={groupAllSelected}
                    data-state={groupPartial ? 'indeterminate' : undefined}
                    onCheckedChange={() => toggleGroup(group)}
                  />
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: MENU_COLOR[group.menu] || colors.text.muted,
                  }} />
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: colors.text.primary }}>
                    {group.menu_label}
                  </h3>
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: colors.text.secondary,
                    padding: '2px 8px', borderRadius: 999, background: colors.background.page,
                  }}>
                    {group.count}건
                    {groupSelectedCount > 0 && <span style={{ color: '#4338ca', marginLeft: 4 }}>· {groupSelectedCount} 선택</span>}
                  </span>
                </div>

                {group.items.map((item) => {
                  const k = keyOf(group.menu, item.id);
                  const checked = selectedKeys.has(k);
                  return (
                    <div
                      key={k}
                      style={{
                        display: 'flex', alignItems: 'center', gap: spacing.md,
                        padding: `${spacing.md} ${spacing.sm}`,
                        background: checked ? '#f5f3ff' : (kpi === 'overdue' && item.overdue ? '#fef7f7' : 'transparent'),
                        borderBottom: `1px solid ${colors.border.light}`,
                        borderRadius: borderRadius.sm,
                        transition: 'background 0.12s ease',
                      }}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleItem(group.menu, item.id)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: colors.text.primary,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {item.title}
                          {item.customer_name && (
                            <span style={{ fontSize: 13, fontWeight: 400, color: colors.text.secondary, marginLeft: 8 }}>
                              · {item.customer_name}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: colors.text.muted, marginTop: 2 }}>
                          {item.assigned_to_name && <>담당 {item.assigned_to_name} · </>}
                          상태 {getStatusLabel(group.menu, item.status)}
                        </div>
                      </div>
                      <div style={{
                        flexShrink: 0, fontSize: 12, fontWeight: 600,
                        padding: '4px 10px', borderRadius: 999,
                        background: item.overdue ? '#fee2e2' : colors.background.page,
                        color: item.overdue ? '#b91c1c' : colors.text.secondary,
                        whiteSpace: 'nowrap',
                      }}>
                        경과 {item.age_days}일
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDetail(item.detail_url)}
                        style={{
                          flexShrink: 0,
                          fontSize: 12, fontWeight: 500,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <ExternalLink size={13} />
                        상세 열기
                      </Button>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>닫기</Button>
        </DialogFooter>
      </DialogContent>

      {/* Bulk sub-dialogs */}
      {statusChangeSupported && singleMenu && (
        <BulkStatusDialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          items={selectedItems.filter((it) => it.menu === singleMenu)}
          menu={singleMenu}
          menuLabel={selectedItems[0]?.menu_label || singleMenu}
          onSuccess={() => { load(); }}
        />
      )}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        items={selectedItems}
        onSuccess={() => { load(); }}
      />
    </Dialog>
  );
};

export default DrillDownModal;
